import * as React from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { CaretDown, Check, MagnifyingGlass, X } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { inputGroupVariants } from './input-group'
import { useFieldStatus, type FieldStatus } from './field'
import { Checkbox } from './checkbox'

/**
 * Select — a value picker (single or multi, plain or searchable) built to DS
 * convention: the trigger reuses the `InputGroup` control look (border + focus
 * ring + size), and the dropdown reuses the `DropdownMenu` panel + leading-✓
 * (single) / `Checkbox` (multi) row styling. Popover-backed (Radix) for
 * positioning, outside-dismiss, and Escape.
 *
 *   single, plain:       <Select options={...} value={v} onValueChange={setV} />
 *   multi, searchable:   <Select options={...} multiple searchable
 *                                value={vs} onValueChange={setVs} />
 *
 *   options    { value, label, icon?, disabled? }[]
 *   multiple   value is string[] (vs string); rows show a checkbox, menu stays open
 *   searchable adds a filter input that matches option labels
 *   size       sm | md | lg     status  default | error | success
 *   clearable  trailing ✕ to reset the selection
 *
 * Designed for filter modals — controlled (`value` + `onValueChange`) or
 * uncontrolled (`defaultValue`). `onValueChange` gets a `string` (single) or
 * `string[]` (multi).
 */

export interface SelectOption {
  value: string
  label: string
  icon?: React.ReactNode
  disabled?: boolean
}

type SelectValue = string | string[]

export interface SelectProps {
  options: SelectOption[]
  value?: SelectValue
  defaultValue?: SelectValue
  onValueChange?: (value: any) => void
  /** Allow multiple selections (value becomes `string[]`). */
  multiple?: boolean
  /** Show a search field that filters options by label. */
  searchable?: boolean
  placeholder?: string
  searchPlaceholder?: string
  /** Shown when the search matches nothing. */
  emptyMessage?: string
  size?: 'sm' | 'md' | 'lg'
  status?: FieldStatus
  disabled?: boolean
  /** Show a trailing ✕ to clear the selection. */
  clearable?: boolean
  /**
   * Close the menu after a single selection. Defaults to `false` so the menu
   * stays open and you can see the ✓ land (close via click-out or Escape).
   * Ignored for `multiple`, which always stays open.
   */
  closeOnSelect?: boolean
  /** Classes on the trigger control. */
  className?: string
}

const toArray = (v?: SelectValue): string[] =>
  v == null || v === '' ? [] : Array.isArray(v) ? v.filter(Boolean) : [v]

const optionRowBase =
  'flex cursor-pointer items-center rounded-lg text-fbui-gray-700 outline-none select-none'

export const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      options,
      value,
      defaultValue,
      onValueChange,
      multiple = false,
      searchable = false,
      placeholder = 'Select…',
      searchPlaceholder = 'Search…',
      emptyMessage = 'No results',
      size = 'md',
      status,
      disabled = false,
      clearable = false,
      closeOnSelect = false,
      className,
    },
    ref
  ) => {
    const resolvedStatus = useFieldStatus(status ?? undefined)
    // A small control gets a proportionally small menu (rows, text, indicators).
    const compact = size === 'sm'
    const rowSize = compact
      ? 'gap-2 px-2.5 py-1.5 text-xs'
      : 'gap-2.5 px-3 py-2 text-sm'
    const indicatorSize = compact ? 'size-3.5' : 'size-4'
    const [open, setOpen] = React.useState(false)
    const [query, setQuery] = React.useState('')
    const [activeIndex, setActiveIndex] = React.useState(0)
    const isControlled = value !== undefined
    const [internal, setInternal] = React.useState<string[]>(
      toArray(defaultValue)
    )
    const selected = isControlled ? toArray(value) : internal
    const optionRefs = React.useRef<(HTMLDivElement | null)[]>([])

    const filtered =
      searchable && query
        ? options.filter((o) =>
            o.label.toLowerCase().includes(query.toLowerCase())
          )
        : options

    const commit = (next: string[]) => {
      if (!isControlled) setInternal(next)
      onValueChange?.(multiple ? next : (next[0] ?? ''))
    }

    const handleSelect = (opt: SelectOption) => {
      if (opt.disabled) return
      if (multiple) {
        commit(
          selected.includes(opt.value)
            ? selected.filter((v) => v !== opt.value)
            : [...selected, opt.value]
        )
      } else {
        commit([opt.value])
        if (closeOnSelect) setOpen(false)
      }
    }

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation()
      commit([])
    }

    const moveActive = (dir: 1 | -1) => {
      const n = filtered.length
      if (!n) return
      let i = activeIndex
      for (let step = 0; step < n; step++) {
        i = (i + dir + n) % n
        if (!filtered[i].disabled) break
      }
      setActiveIndex(i)
      optionRefs.current[i]?.scrollIntoView({ block: 'nearest' })
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        moveActive(1)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        moveActive(-1)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const opt = filtered[activeIndex]
        if (opt) handleSelect(opt)
      }
    }

    const selectedOptions = selected
      .map((v) => options.find((o) => o.value === v))
      .filter((o): o is SelectOption => Boolean(o))
    const hasSelection = selected.length > 0
    const showClear = clearable && hasSelection && !disabled
    const displayText = !hasSelection
      ? placeholder
      : multiple
        ? selected.length === 1
          ? selectedOptions[0]?.label
          : `${selected.length} selected`
        : selectedOptions[0]?.label

    return (
      <PopoverPrimitive.Root
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) {
            setQuery('')
            setActiveIndex(0)
          }
        }}
      >
        <div className="relative">
          <PopoverPrimitive.Trigger asChild>
            <button
              ref={ref}
              type="button"
              disabled={disabled}
              aria-haspopup="listbox"
              aria-expanded={open}
              className={cn(
                inputGroupVariants({ status: resolvedStatus, size }),
                'pl-3 text-left disabled:cursor-not-allowed disabled:bg-fbui-gray-50',
                showClear ? 'pr-14' : 'pr-9',
                className
              )}
            >
              <span
                className={cn(
                  'flex-1 truncate',
                  disabled
                    ? 'text-fbui-text-disabled'
                    : hasSelection
                      ? 'text-fbui-gray-800'
                      : 'text-fbui-gray-400',
                  size === 'sm' ? 'text-xs' : 'text-sm'
                )}
              >
                {displayText}
              </span>
            </button>
          </PopoverPrimitive.Trigger>
          {/* Icons overlay the trigger; the container is click-through so the
              caret still opens the menu, while the clear button (a real,
              focusable <button> — not nested in the trigger) re-enables clicks. */}
          <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center gap-1">
            {showClear && (
              <button
                type="button"
                onClick={handleClear}
                aria-label="Clear selection"
                className="pointer-events-auto inline-flex size-5 shrink-0 items-center justify-center rounded text-fbui-gray-400 outline-none transition-colors hover:bg-fbui-gray-100 hover:text-fbui-gray-600 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand"
              >
                <X weight="bold" className="size-3.5" aria-hidden="true" />
              </button>
            )}
            <CaretDown
              weight="bold"
              aria-hidden="true"
              className="size-3.5 shrink-0 text-fbui-gray-400"
            />
          </div>
        </div>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="start"
            sideOffset={6}
            onKeyDown={searchable ? undefined : handleKeyDown}
            style={{ width: 'var(--radix-popover-trigger-width)' }}
            className={cn(
              'z-50 overflow-hidden rounded-xl border border-fbui-gray-200 bg-fbui-base-white p-1.5 shadow-fbui-lg',
              'origin-[--radix-popover-content-transform-origin]'
            )}
          >
            {searchable && (
              <div className="mb-1 flex items-center gap-2 border-b border-fbui-gray-100 px-2 pb-1.5">
                <MagnifyingGlass
                  aria-hidden="true"
                  className={cn('shrink-0 text-fbui-gray-400', indicatorSize)}
                />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value)
                    setActiveIndex(0)
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={searchPlaceholder}
                  className={cn(
                    'w-full bg-transparent py-1 text-fbui-gray-800 outline-none placeholder:text-fbui-gray-400',
                    compact ? 'text-xs' : 'text-sm'
                  )}
                />
              </div>
            )}

            <div
              role="listbox"
              aria-multiselectable={multiple || undefined}
              className="max-h-72 overflow-y-auto"
            >
              {filtered.length === 0 ? (
                <div
                  className={cn(
                    'px-3 text-center text-fbui-gray-400',
                    compact ? 'py-4 text-xs' : 'py-6 text-sm'
                  )}
                >
                  {emptyMessage}
                </div>
              ) : (
                filtered.map((opt, i) => {
                  const isSelected = selected.includes(opt.value)
                  const isActive = i === activeIndex
                  return (
                    <div
                      key={opt.value}
                      ref={(el) => {
                        optionRefs.current[i] = el
                      }}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handleSelect(opt)}
                      onMouseEnter={() => setActiveIndex(i)}
                      className={cn(
                        optionRowBase,
                        rowSize,
                        isActive && 'bg-fbui-gray-100 text-fbui-gray-900',
                        opt.disabled && 'pointer-events-none text-fbui-gray-300'
                      )}
                    >
                      {multiple ? (
                        <Checkbox
                          size="sm"
                          checked={isSelected}
                          readOnly
                          tabIndex={-1}
                          className="pointer-events-none"
                        />
                      ) : (
                        <span
                          className={cn(
                            'flex shrink-0 items-center justify-center',
                            indicatorSize
                          )}
                        >
                          {isSelected && (
                            <Check
                              weight="bold"
                              aria-hidden="true"
                              className={indicatorSize}
                            />
                          )}
                        </span>
                      )}
                      {opt.icon && (
                        <span
                          className={cn(
                            'flex shrink-0 items-center justify-center text-fbui-gray-500',
                            indicatorSize,
                            compact ? '[&>svg]:size-3.5' : '[&>svg]:size-4'
                          )}
                        >
                          {opt.icon}
                        </span>
                      )}
                      <span className="flex-1 truncate">{opt.label}</span>
                    </div>
                  )
                })
              )}
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    )
  }
)
Select.displayName = 'Select'
